import { z } from 'zod';
// The published catalogue: what an app offers, at which provider, and at what
// rate. Produced offline by the app's `build-catalog` script from the shared
// catalogue (`packages/models`), reviewed as a diff, then published to a
// running deployment — never read from a file at request time.
//
// Publishing is what decouples a price move from a deploy. A deploy restarts
// the workers, which kills the agentic loops in flight; a barème has no reason
// to cost that.
//
// The shape mirrors `Catalog` in `packages/models/src/catalog/types.ts` and
// extends it with what only an app knows: which models it offers, in which
// order it prefers their providers, and what each one costs in credits. Objects
// are LOOSE on purpose — the shared catalogue grows fields on its own schedule,
// and an unknown one must travel through publication rather than be stripped
// here.
/**
 * Credits per million tokens, over the DISJOINT leaves of TokenUsage. Mirrors
 * that split exactly so a usage report is priced by a weighted sum with no
 * double counting.
 */
export const RateCardSchema = z.object({
    input: z.object({
        uncached: z.number().nonnegative(),
        cacheRead: z.number().nonnegative(),
        cacheWrite: z.number().nonnegative()
    }),
    output: z.object({
        text: z.number().nonnegative(),
        reasoning: z.number().nonnegative()
    })
});
export const CatalogVendorSchema = z.looseObject({
    id: z.string().min(1),
    label: z.string().min(1),
    country: z.string().optional()
});
export const CatalogProviderSchema = z.looseObject({
    id: z.string().min(1),
    label: z.string().min(1)
});
export const CatalogModelSchema = z.looseObject({
    // Canonical market identity, `vendor/model` — never a provider id.
    id: z.string().min(1),
    vendorId: z.string().min(1),
    label: z.string().min(1),
    // Whether the app proposes it. A model that is NOT offered stays published:
    // recorded messages name it (`messages.model`), and their display has to keep
    // working. This is the line between what is usable and what is historical,
    // and it is a decision — distinct from `served`, which is what the provider
    // does.
    offered: z.boolean(),
    capabilities: z.array(z.string()).default([]),
    contextWindow: z.number().int().positive().optional(),
    // Harvested benchmark scores, absent when the source has none for this model.
    scores: z.looseObject({
        intelligence: z.number().optional(),
        coding: z.number().optional(),
        agentic: z.number().optional()
    }).optional()
});
export const CatalogOfferingSchema = z.looseObject({
    providerId: z.string().min(1),
    modelId: z.string().min(1),
    // Absent when the app runs without a barème (no billing wired). An offered
    // model with no rate card is refused at publication when the app does bill.
    rateCard: RateCardSchema.optional(),
    // One comparable number per offering, for ranking them against each other —
    // never for billing one. Published as a fact; what a router does with it is
    // the router's business.
    blendedCredits: z.number().nonnegative().optional(),
    // What THIS provider serves, which is not what the model supports: the same
    // weights are offered with a narrower window in plenty of places. Carried per
    // offering because routing needs to compare two of them — moving a running
    // conversation to a provider serving less than the one it started on would
    // cut its history off.
    //
    // Absent when the provider does not publish it. Read as "unknown", never as
    // "unlimited": a candidate that promises nothing is not a candidate.
    contextWindow: z.number().int().positive().optional()
});
export const PublishedCatalogSchema = z.looseObject({
    // Names this barème. Frozen on every debit (`credit_movements.rate_version`),
    // so it must move whenever the content moves — publication refuses to reuse a
    // version for a different document.
    version: z.string().min(1).max(120),
    updatedAt: z.iso.datetime(),
    // Which model plays which part. Published rather than configured, so changing
    // any of them does not need a deploy — and a deploy restarts the workers,
    // which kills the agentic loops in flight.
    //
    // `default` is what a conversation gets when neither it nor its user has an
    // opinion, so it has to be offered. `title` and `summary` are internal
    // overhead and never a product choice — a user picks the model they talk to,
    // not the one that names their conversation — so they only have to be served,
    // and they fall back to `default`.
    roles: z.object({
        default: z.string().min(1),
        title: z.string().min(1).optional(),
        summary: z.string().min(1).optional()
    }),
    vendors: z.array(CatalogVendorSchema),
    providers: z.array(CatalogProviderSchema),
    models: z.array(CatalogModelSchema),
    // One entry per (provider, model) actually served. Facts only, in no
    // meaningful order: which provider a conversation ends up on is a routing
    // rule, and a rule belongs in code, not in the shape of an array.
    offerings: z.array(CatalogOfferingSchema)
});
/** Lookup key of a rate card. Joins the journal's two columns for lookup only —
 * they are recorded separately and never concatenated. */
export function offeringKey(providerId, modelId) {
    return `${providerId}/${modelId}`;
}
/**
 * Why a model is or is not selectable.
 *
 * Three states rather than a boolean: "we stopped proposing this one" and "your
 * organisation does not allow it" are opposite messages, and collapsing them
 * leaves the front unable to say either.
 */
export const ModelStatusSchema = z.enum([
    // Selectable.
    'offered',
    // Withdrawn from the offer. Still published so a recorded message keeps a
    // label, and so a preference naming it can be shown for what it is.
    'retired',
    // Offered, but no provider is allowed to serve it for this subject.
    'restricted'
]);
/**
 * A model as the front sees it, derived from the published catalogue for one
 * subject.
 *
 * Models that cannot be selected travel too, and say why. A user whose default
 * was withdrawn has to be told which model that was — not shown a dangling id —
 * and a message recorded under it still has to display a label.
 */
export const OfferedModelSchema = z.object({
    id: z.string(),
    label: z.string(),
    vendorLabel: z.string(),
    capabilities: z.array(z.string()),
    contextWindow: z.number().int().positive().optional(),
    // What this model costs relative to the default, on the published price
    // indicator. Derived when the offer is listed, not published: how a cost is
    // presented is a display choice, and the document carries facts and binding
    // decisions — a change of design has no business republishing a barème.
    // Absent when the deployment prices nothing.
    costRatio: z.number().positive().optional(),
    // Absent when the benchmark source has no score for this model.
    intelligence: z.number().optional(),
    status: ModelStatusSchema,
    isDefault: z.boolean()
});
